import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { googleLogout } from '@react-oauth/google';
import { authApi, GoogleExchangeResponse } from '../api/authApi';
import { facebookLogout } from './facebookAuth';
import { emailLoginRequest, identityTokenRequest } from './msalConfig';
import { getMicrosoftIdToken, microsoftLogout } from './microsoftAuth';
import { useDevAuth } from './DevAuthProvider';

const EXTERNAL_SESSION_KEY = 'caddiesight.auth.external';

/** The signed-in golfer's identity claims, normalized across auth providers. */
export interface AuthUser {
  userId?: string;
  name?: string;
  email?: string;
  picture?: string;
  provider?: string;
}

interface GoogleSession {
  accessToken: string;
  expiresAt: string;
  user: AuthUser;
  identityProvider: 'direct' | 'entra';
}

/**
 * Unified auth surface for Entra, direct Google sessions, and the dev bypass.
 * Consumers import this hook rather than an identity-provider hook directly.
 */
export interface AuthState {
  isAuthenticated: boolean;
  /** True while MSAL is resolving the session (startup / redirect handling). */
  isLoading: boolean;
  /** User-facing error from completing an Entra sign-in or API session exchange. */
  authError?: string;
  user?: AuthUser;
  /** True for Entra local email/password accounts, not federated social identities. */
  canResetPassword: boolean;
  /** Starts the Entra sign-in showing all identity providers (email last). */
  loginEmail: () => void;
  /** Authenticates a personal Microsoft account and starts a CaddieSight session. */
  loginMicrosoft: () => Promise<void>;
  /** Exchanges a Google Identity Services credential for an API session. */
  loginGoogle: (idToken: string) => Promise<void>;
  /** Exchanges a Facebook user access token for an API session. */
  loginFacebook: (accessToken: string) => Promise<void>;
  /** Exchanges an Apple identity token for an API session. */
  loginApple: (identityToken: string, nonce: string, displayName?: string) => Promise<void>;
  /** Exchanges a native-app browser handoff for an API session. */
  loginClubhouseHandoff: (token: string) => Promise<void>;
  /** Links Google to the current account without replacing the active session. */
  linkGoogle: (idToken: string) => Promise<void>;
  /** Links Facebook to the current account without replacing the active session. */
  linkFacebook: (accessToken: string) => Promise<void>;
  /** Links Apple to the current account without replacing the active session. */
  linkApple: (identityToken: string, nonce: string, displayName?: string) => Promise<void>;
  /** Signs out the active provider and clears any first-party local session. */
  logout: () => void;
  /** Silently acquires a CaddieSight API access token, or null when unavailable. */
  acquireToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const externalEmail = (value?: string): string | undefined => {
  const email = value?.trim();
  return email && !email.endsWith('.onmicrosoft.com') ? email : undefined;
};

function normalizeExternalSession(
  response: GoogleExchangeResponse,
  fallbackProvider: string,
  identityProvider: GoogleSession['identityProvider'] = 'direct',
): GoogleSession {
  return {
    accessToken: response.accessToken,
    expiresAt: response.expiresAt,
    identityProvider,
    user: {
      userId: response.user.userId,
      name: response.user.name?.trim() || undefined,
      email: response.user.email?.trim() || undefined,
      picture: response.user.picture?.trim() || undefined,
      provider: response.user.provider?.trim() || fallbackProvider,
    },
  };
}

function isValidGoogleSession(value: unknown): value is GoogleSession {
  if (!value || typeof value !== 'object') return false;
  const session = value as GoogleSession;
  return Boolean(
    session.accessToken &&
      session.expiresAt &&
      Date.parse(session.expiresAt) > Date.now() &&
      session.user?.userId,
  );
}

function restoreGoogleSession(): GoogleSession | null {
  try {
    const raw = localStorage.getItem(EXTERNAL_SESSION_KEY);
    if (!raw) return null;
    const session: unknown = JSON.parse(raw);
    if (isValidGoogleSession(session)) return session;
    localStorage.removeItem(EXTERNAL_SESSION_KEY);
  } catch {
    localStorage.removeItem(EXTERNAL_SESSION_KEY);
  }
  return null;
}

function isEntraLocalAccount(account: ReturnType<typeof useMsal>['accounts'][number] | undefined): boolean {
  if (!account) return false;
  const identityProvider = account.idTokenClaims?.idp;
  if (typeof identityProvider !== 'string' || !identityProvider.trim()) return true;

  const normalizedProvider = identityProvider.toLowerCase();
  const issuer = typeof account.idTokenClaims?.iss === 'string'
    ? account.idTokenClaims.iss.toLowerCase()
    : '';
  return normalizedProvider === 'localaccount'
    || normalizedProvider === issuer
    || normalizedProvider.includes('ciamlogin.com');
}

/**
 * Composes direct Google sessions with MSAL. MSAL hooks are always called in the
 * same order; a valid first-party Google session takes precedence when present.
 */
export const AuthSessionProvider: React.FC<{ children: ReactNode; entraIdToken?: string }> = ({
  children,
  entraIdToken,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const account = accounts[0];
  const [googleSession, setGoogleSession] = useState<GoogleSession | null>(restoreGoogleSession);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [entraExchangeError, setEntraExchangeError] = useState<string>();

  useEffect(() => {
    if (!isMsalAuthenticated || !account || googleSession || inProgress !== InteractionStatus.None) return;

    let cancelled = false;
    setIsGoogleLoading(true);
    setEntraExchangeError(undefined);
    const idTokenPromise = entraIdToken
      ? Promise.resolve(entraIdToken)
      : instance.acquireTokenSilent({ ...identityTokenRequest, account }).then((result) => result.idToken);
    idTokenPromise
      .then((idToken) => authApi.exchangeEntraCredential(idToken))
      .then((response) => {
        if (cancelled) return;
        const session = normalizeExternalSession(response, 'entra', 'entra');
        if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
        localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
        setGoogleSession(session);
      })
      .catch((error) => {
        if (cancelled) return;
        // A cached account with no live Entra session (AADSTS160021) can't be resolved silently — MSAL
        // needs an interactive round-trip. Redirect to re-establish it instead of dead-ending on "try
        // again", which would just hit the same silent failure.
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({ scopes: identityTokenRequest.scopes, account }).catch((redirectError) => {
            if (cancelled) return;
            console.error('Entra interactive sign-in failed', redirectError);
            setEntraExchangeError(
              'Sign-in succeeded, but CaddieSight could not start your session. Please try again.',
            );
          });
          return;
        }
        console.error('Entra session exchange failed', error);
        setEntraExchangeError(
          'Sign-in succeeded, but CaddieSight could not start your session. Please try again.',
        );
      })
      .finally(() => {
        if (!cancelled) setIsGoogleLoading(false);
      });

    return () => { cancelled = true; };
  }, [isMsalAuthenticated, account, googleSession, inProgress, instance, entraIdToken]);

  const loginEmail = useCallback(() => {
    setEntraExchangeError(undefined);
    instance.loginRedirect(emailLoginRequest).catch((error) => {
      // Surface the reason the redirect never left (e.g. unregistered redirect
      // URI, interaction already in progress) instead of failing silently.
      // eslint-disable-next-line no-console
      console.error('Entra sign-in redirect failed', error);
    });
  }, [instance]);

  const loginMicrosoft = useCallback(async () => {
    setIsGoogleLoading(true);
    setEntraExchangeError(undefined);
    try {
      const idToken = await getMicrosoftIdToken();
      const session = normalizeExternalSession(await authApi.exchangeMicrosoftCredential(idToken), 'microsoft');
      if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
      localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
      setGoogleSession(session);
    } finally {
      setIsGoogleLoading(false);
    }
  }, []);

  const loginGoogle = useCallback(async (idToken: string) => {
    setIsGoogleLoading(true);
    try {
      const session = normalizeExternalSession(await authApi.exchangeGoogleCredential(idToken), 'google');
      if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
      localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
      setGoogleSession(session);
    } finally {
      setIsGoogleLoading(false);
    }
  }, []);

  const loginFacebook = useCallback(async (accessToken: string) => {
    setIsGoogleLoading(true);
    try {
      const session = normalizeExternalSession(await authApi.exchangeFacebookCredential(accessToken), 'facebook');
      if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
      localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
      setGoogleSession(session);
    } finally {
      setIsGoogleLoading(false);
    }
  }, []);

  const loginApple = useCallback(async (identityToken: string, nonce: string, displayName?: string) => {
    setIsGoogleLoading(true);
    try {
      const response = await authApi.exchangeAppleCredential(identityToken, nonce, displayName);
      const session = normalizeExternalSession(response, 'apple');
      if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
      localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
      setGoogleSession(session);
    } finally {
      setIsGoogleLoading(false);
    }
  }, []);

  const loginClubhouseHandoff = useCallback(async (token: string) => {
    setIsGoogleLoading(true);
    setEntraExchangeError(undefined);
    try {
      const session = normalizeExternalSession(
        await authApi.exchangeClubhouseHandoff(token),
        'session',
      );
      if (!isValidGoogleSession(session)) throw new Error('The API returned an invalid or expired session.');
      localStorage.setItem(EXTERNAL_SESSION_KEY, JSON.stringify(session));
      setGoogleSession(session);
    } finally {
      setIsGoogleLoading(false);
    }
  }, []);

  const linkGoogle = useCallback(async (idToken: string) => {
    await authApi.linkGoogleCredential(idToken);
  }, []);

  const linkFacebook = useCallback(async (accessToken: string) => {
    await authApi.linkFacebookCredential(accessToken);
  }, []);

  const linkApple = useCallback(async (identityToken: string, nonce: string, displayName?: string) => {
    await authApi.linkAppleCredential(identityToken, nonce, displayName);
  }, []);

  const logout = useCallback(() => {
    if (googleSession) {
      localStorage.removeItem(EXTERNAL_SESSION_KEY);
      setGoogleSession(null);
      if (googleSession.identityProvider === 'entra') {
        instance.logoutRedirect().catch(() => {
          /* MSAL clears its local state during redirect logout. */
        });
        return;
      }
      if (googleSession.user.provider === 'microsoft') void microsoftLogout();
      else if (googleSession.user.provider === 'facebook') facebookLogout();
      else googleLogout();
      return;
    }
    instance.logoutRedirect().catch(() => {
      /* ignore; MSAL clears its local state during redirect logout */
    });
  }, [googleSession, instance]);

  const acquireToken = useCallback(async () => {
    if (googleSession) {
      if (Date.parse(googleSession.expiresAt) > Date.now()) return googleSession.accessToken;
      localStorage.removeItem(EXTERNAL_SESSION_KEY);
      setGoogleSession(null);
      return null;
    }
    return null;
  }, [googleSession]);

  useEffect(() => {
    if (!googleSession) return;
    const remaining = Date.parse(googleSession.expiresAt) - Date.now();
    const timeout = window.setTimeout(() => {
      localStorage.removeItem(EXTERNAL_SESSION_KEY);
      setGoogleSession(null);
    }, Math.min(remaining, 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [googleSession]);

  const value = useMemo<AuthState>(() => ({
    isAuthenticated: Boolean(googleSession),
    isLoading: isGoogleLoading
      || inProgress !== InteractionStatus.None
      || (isMsalAuthenticated && !googleSession && !entraExchangeError),
    authError: entraExchangeError,
    user: googleSession?.user ?? (account
      ? { name: account.name?.trim() || undefined, email: externalEmail(account.username), provider: 'entra' }
      : undefined),
    canResetPassword: googleSession?.identityProvider === 'entra' && isEntraLocalAccount(account),
    loginEmail,
    loginMicrosoft,
    loginGoogle,
    loginFacebook,
    loginApple,
    loginClubhouseHandoff,
    linkGoogle,
    linkFacebook,
    linkApple,
    logout,
    acquireToken,
  }), [
    googleSession,
    isMsalAuthenticated,
    isGoogleLoading,
    inProgress,
    entraExchangeError,
    account,
    loginEmail,
    loginMicrosoft,
    loginGoogle,
    loginFacebook,
    loginApple,
    loginClubhouseHandoff,
    linkGoogle,
    linkFacebook,
    linkApple,
    logout,
    acquireToken,
  ]);

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const DevAuthSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, signIn, signOut, user } = useDevAuth();
  const value = useMemo<AuthState>(() => ({
    isAuthenticated,
    isLoading: false,
    authError: undefined,
    user,
    canResetPassword: false,
    loginEmail: signIn,
    loginMicrosoft: async () => signIn(),
    loginGoogle: async () => signIn(),
    loginFacebook: async () => signIn(),
    loginApple: async () => signIn(),
    loginClubhouseHandoff: async () => signIn(),
    // Keep local bypass behavior coherent without attempting an external link.
    linkGoogle: async () => undefined,
    linkFacebook: async () => undefined,
    linkApple: async () => undefined,
    logout: signOut,
    // No real token is needed — the API's dev auth handler authenticates every request.
    acquireToken: async () => null,
  }), [isAuthenticated, signIn, signOut, user]);

  return React.createElement(AuthContext.Provider, { value }, children);
};

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
